import React from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Trash2, ShoppingBag, ArrowRight } from 'lucide-react';
import { useCart } from '../../context/CartContext';
import { useAuth } from '../../context/AuthContext';
import { toast } from 'react-hot-toast';

const Cart: React.FC = () => {
  const { items, removeItem, updateQuantity, clearCart, totalPrice } = useCart();
  const { user } = useAuth();
  const navigate = useNavigate();
  
  const handleCheckout = () => {
    if (!user) {
      toast.error('Log in om door te gaan');
      navigate('/login', { state: { from: '/checkout' } });
      return;
    }
    
    navigate('/checkout');
  };
  
  if (items.length === 0) {
    return (
      <div className="container mx-auto px-4 py-8 mt-8">
        <h1 className="text-3xl font-bold mb-8">Uw winkelwagen</h1>
        <div className="bg-white rounded-lg shadow-md p-8 text-center">
          <ShoppingBag className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h2 className="text-2xl font-semibold mb-4">Uw winkelwagen is leeg</h2>
          <p className="text-gray-600 mb-6">
            U heeft nog geen contracten aan uw winkelwagen toegevoegd.
          </p>
          <Link
            to="/contracts"
            className="bg-blue-600 hover:bg-blue-700 text-white font-medium py-2 px-6 rounded-md transition duration-300"
          >
            Bekijk de catalogus
          </Link>
        </div>
      </div>
    );
  }
  
  return (
    <div className="container mx-auto px-4 py-8 mt-8">
      <h1 className="text-3xl font-bold mb-8">Uw winkelwagen</h1>
      
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow-md overflow-hidden">
            <div className="p-6">
              <div className="flow-root">
                <ul className="divide-y divide-gray-200">
                  {items.map((item) => (
                    <li key={item.contract.id} className="py-6 flex">
                      <div className="flex-1 flex flex-col">
                        <div>
                          <div className="flex justify-between text-base font-medium text-gray-900">
                            <h3>
                              <Link to={`/contracts/${item.contract.id}`} className="hover:text-blue-600">
                                {item.contract.name}
                              </Link>
                            </h3>
                            <p className="ml-4">{item.contract.price * item.quantity} €</p>
                          </div>
                          <p className="mt-1 text-sm text-gray-500 line-clamp-2">
                            {item.contract.description}
                          </p>
                        </div>
                        <div className="flex-1 flex items-end justify-between text-sm">
                          <div className="flex items-center">
                            <label htmlFor={`quantity-${item.contract.id}`} className="mr-2 text-gray-600">
                              Aantal
                            </label>
                            <select
                              id={`quantity-${item.contract.id}`}
                              value={item.quantity}
                              onChange={(e) => updateQuantity(item.contract.id, parseInt(e.target.value))}
                              className="border border-gray-300 rounded-md p-1"
                            >
                              {[1, 2, 3, 4, 5].map((num) => (
                                <option key={num} value={num}>
                                  {num}
                                </option>
                              ))}
                            </select>
                          </div>
                          <div className="flex">
                            <button
                              type="button"
                              onClick={() => removeItem(item.contract.id)}
                              className="font-medium text-red-600 hover:text-red-500 flex items-center"
                            >
                              <Trash2 className="h-4 w-4 mr-1" />
                              Verwijderen
                            </button>
                          </div>
                        </div>
                      </div>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="border-t border-gray-200 pt-4 flex justify-between">
                <button
                  type="button"
                  onClick={clearCart}
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Winkelwagen leegmaken
                </button>
                <Link
                  to="/contracts"
                  className="font-medium text-blue-600 hover:text-blue-500"
                >
                  Verder winkelen
                </Link>
              </div>
            </div>
          </div>
        </div>
        
        <div className="lg:col-span-1">
          <div className="bg-white rounded-lg shadow-md p-6">
            <h2 className="text-lg font-semibold mb-4">Besteloverzicht</h2>
            
            <div className="flow-root">
              <dl className="space-y-4">
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-gray-600">Subtotaal</dt>
                  <dd className="text-sm font-medium text-gray-900">{totalPrice} €</dd>
                </div>
                <div className="flex items-center justify-between">
                  <dt className="text-sm text-gray-600">BTW (21%)</dt>
                  <dd className="text-sm font-medium text-gray-900">{(totalPrice * 0.21).toFixed(2)} €</dd>
                </div>
                <div className="border-t border-gray-200 pt-4 flex items-center justify-between">
                  <dt className="text-base font-medium text-gray-900">Totaal</dt>
                  <dd className="text-base font-medium text-gray-900">{(totalPrice * 1.21).toFixed(2)} €</dd>
                </div>
              </dl>
            </div>
            
            <div className="mt-6">
              <button
                onClick={handleCheckout}
                className="w-full flex items-center justify-center bg-blue-600 hover:bg-blue-700 text-white font-medium py-3 px-4 rounded-md transition duration-300"
              >
                Naar afrekenen
                <ArrowRight className="ml-2 h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Cart;